/* ------------------------------------------------------------------ */
/*  Rule engine – evaluate rules against decoded log lines            */
/* ------------------------------------------------------------------ */

import type { WazuhRule, DecodedLog, FiredRule, ParseMessage } from './types';

function compileRegex(
  pattern: string,
  messages: ParseMessage[],
  context: string
): RegExp | null {
  try {
    return new RegExp(pattern);
  } catch (e) {
    messages.push({
      type: 'error',
      message: `${context}: Invalid regex "${pattern}" — ${(e as Error).message}`,
    });
    return null;
  }
}

interface CompiledRule {
  rule: WazuhRule;
  programNameRx?: RegExp;
  regexRx?: RegExp;
}

function compileRules(rules: WazuhRule[], messages: ParseMessage[]): CompiledRule[] {
  const compiled: CompiledRule[] = [];

  for (const rule of rules) {
    const programNameRx = rule.program_name
      ? compileRegex(rule.program_name, messages, `Rule ${rule.id} program_name`)
      : undefined;
    if (rule.program_name && !programNameRx) continue;

    const regexRx = rule.regex
      ? compileRegex(rule.regex, messages, `Rule ${rule.id} regex`)
      : undefined;
    if (rule.regex && !regexRx) continue;

    compiled.push({ rule, programNameRx, regexRx });
  }

  return compiled;
}

/** Check if a single rule's conditions match a decoded log line */
function ruleMatchesLine(compiled: CompiledRule, decoded: DecodedLog): boolean {
  const { rule, programNameRx, regexRx } = compiled;

  // decoded_as: must match the decoder name
  if (rule.decoded_as) {
    if (decoded.decoder !== rule.decoded_as) return false;
  }

  // program_name: regex match on program_name
  if (programNameRx && !programNameRx.test(decoded.program_name ?? '')) {
    return false;
  }

  // match: substring match against raw log
  if (rule.match) {
    if (!decoded.raw.includes(rule.match)) return false;
  }

  // regex: regex match against raw log
  if (regexRx && !regexRx.test(decoded.raw)) {
    return false;
  }

  // srcip: match on extracted srcip field
  if (rule.srcip) {
    if (decoded.fields['srcip'] !== rule.srcip) return false;
  }

  return true;
}

/** Convert a WazuhRule to a FiredRule */
function toFired(rule: WazuhRule): FiredRule {
  return {
    id: rule.id,
    level: rule.level,
    description: rule.description ?? `Rule ${rule.id}`,
    mitre: rule.mitre,
  };
}

function addRuleFire(
  lineIndex: number,
  compiled: CompiledRule,
  result: Map<number, FiredRule[]>,
  firedMap: Map<number, Set<number>>,
  lineFiredRuleIds: Array<Set<number>>
) {
  const { rule } = compiled;
  const firedRules = result.get(lineIndex)!;
  const alreadyFired = firedRules.some((fired) => fired.id === rule.id);
  if (!alreadyFired) {
    firedRules.push(toFired(rule));
  }

  if (!firedMap.has(rule.id)) firedMap.set(rule.id, new Set());
  firedMap.get(rule.id)!.add(lineIndex);
  lineFiredRuleIds[lineIndex].add(rule.id);
}

/**
 * Evaluate all rules against all decoded log lines.
 *
 * Processing order:
 *  1. Non-chained rules (no if_sid) — evaluated per line
 *  2. Non-frequency chained rules (if_sid, no frequency)
 *  3. Frequency rules (if_sid + frequency) — evaluated across all lines
 *
 * Returns a map: lineIndex → FiredRule[]
 */
export function evaluateRules(
  decodedLines: DecodedLog[],
  rules: WazuhRule[],
  messages: ParseMessage[]
): Map<number, FiredRule[]> {
  const result = new Map<number, FiredRule[]>();

  // Initialize result map
  for (let i = 0; i < decodedLines.length; i++) {
    result.set(i, []);
  }

  const compiledRules = compileRules(rules, messages);

  // Partition rules
  const baseRules = compiledRules.filter((r) => !r.rule.if_sid);
  const chainedRules = compiledRules.filter((r) => r.rule.if_sid && !r.rule.frequency);
  const frequencyRules = compiledRules.filter((r) => r.rule.if_sid && r.rule.frequency);

  // Track which rule ids fired on which lines (for chaining)
  const firedMap = new Map<number, Set<number>>(); // ruleId → Set<lineIndex>
  const lineFiredRuleIds = decodedLines.map(() => new Set<number>());

  // 1. Evaluate base rules
  for (let i = 0; i < decodedLines.length; i++) {
    const decoded = decodedLines[i];
    for (const compiled of baseRules) {
      if (ruleMatchesLine(compiled, decoded)) {
        addRuleFire(i, compiled, result, firedMap, lineFiredRuleIds);
      }
    }
  }

  // 2. Evaluate chained rules (if_sid without frequency)
  const warnedMissingParent = new Set<number>();
  for (let i = 0; i < decodedLines.length; i++) {
    const decoded = decodedLines[i];
    for (const compiled of chainedRules) {
      const parentId = compiled.rule.if_sid!;
      const parentFiredAnywhere = firedMap.has(parentId);
      if (!parentFiredAnywhere) {
        if (!warnedMissingParent.has(compiled.rule.id)) {
          messages.push({
            type: 'warning',
            message: `Rule ${compiled.rule.id}: parent rule ${parentId} did not fire; chain not evaluated.`,
          });
          warnedMissingParent.add(compiled.rule.id);
        }
        continue;
      }

      // For if_sid chaining, parent and child must match the same event (line).
      if (!lineFiredRuleIds[i].has(parentId)) continue;

      if (ruleMatchesLine(compiled, decoded)) {
        addRuleFire(i, compiled, result, firedMap, lineFiredRuleIds);
      }
    }
  }

  // 3. Evaluate frequency rules
  for (const compiled of frequencyRules) {
    const rule = compiled.rule;
    const parentId = rule.if_sid!;
    const parentLines = firedMap.get(parentId);
    if (!parentLines || parentLines.size === 0) {
      if (!warnedMissingParent.has(rule.id)) {
        messages.push({
          type: 'warning',
          message: `Rule ${rule.id}: parent rule ${parentId} did not fire; frequency condition not evaluated.`,
        });
        warnedMissingParent.add(rule.id);
      }
      continue;
    }

    // For frequency rules, candidate events must satisfy parent + child predicates.
    const candidateLines = Array.from(parentLines)
      .filter((lineIdx) => ruleMatchesLine(compiled, decodedLines[lineIdx]))
      .sort((a, b) => a - b);

    if (candidateLines.length === 0) continue;

    const threshold = rule.frequency!;
    const timeframe = rule.timeframe;
    const useTimeframe = timeframe !== undefined && timeframe > 0;

    const warnedTimeFallback = new Set<number>();
    const eventSecond = (lineIdx: number): number => {
      const ts = decodedLines[lineIdx].timestampSec;
      if (ts !== undefined) return ts;

      if (useTimeframe && !warnedTimeFallback.has(rule.id)) {
        messages.push({
          type: 'warning',
          message: `Rule ${rule.id}: one or more logs missing syslog timestamps; timeframe uses log order fallback for those lines.`,
        });
        warnedTimeFallback.add(rule.id);
      }

      // Fallback preserves relative ordering for non-syslog lines.
      return lineIdx;
    };

    const groups = new Map<string, number[]>();
    for (const lineIdx of candidateLines) {
      const key = rule.same_source_ip
        ? (decodedLines[lineIdx].fields['srcip'] ?? 'unknown')
        : '__all__';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(lineIdx);
    }

    for (const [, lineIndexes] of groups) {
      if (!useTimeframe) {
        let seen = 0;
        for (const lineIdx of lineIndexes) {
          seen += 1;
          if (seen >= threshold) {
            addRuleFire(lineIdx, compiled, result, firedMap, lineFiredRuleIds);
          }
        }
        continue;
      }

      const window: number[] = [];
      for (const lineIdx of lineIndexes) {
        const currentSec = eventSecond(lineIdx);
        window.push(lineIdx);

        while (window.length > 0 && currentSec - eventSecond(window[0]) > timeframe!) {
          window.shift();
        }

        if (window.length >= threshold) {
          addRuleFire(lineIdx, compiled, result, firedMap, lineFiredRuleIds);
        }
      }
    }
  }

  // Helpful warning for frequency rules that configured timeframe without frequency.
  for (const compiled of compiledRules) {
    const rule = compiled.rule;
    if (rule.timeframe && !rule.frequency) {
      messages.push({
        type: 'warning',
        message: `Rule ${rule.id}: <timeframe> is set without <frequency>; timeframe has no effect.`,
      });
    } else {
      continue;
    }
  }

  return result;
}
